/**
 * ShopifyScrollFX - Enterprise Scroll Animation Library
 * Version: 1.0.0
 * 
 * Production-ready scroll animation system for Shopify storefronts.
 * Zero dependencies, accessible, performant.
 * 
 * @usage Add data-scroll-fx="animation-name" to any element
 * @license MIT
 */

(function(window, document) {
  'use strict';

  var NAMESPACE = 'ShopifyScrollFX';
  var DATA_ATTR = 'data-scroll-fx';
  var INITIALIZED_ATTR = 'data-scroll-fx-init';
  
  var DEFAULTS = {
    debug: false,
    respectReducedMotion: true,
    disableOnMobile: false,
    mobileBreakpoint: 768,
    threshold: 0.15,
    rootMargin: '0px 0px -10% 0px',
    once: true,
    duration: 800,
    delay: 0,
    easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
    stagger: 0,
    useGPU: true
  };

  var ANIMATIONS = {
    'fade-in': {
      initial: { opacity: '0' },
      animate: { opacity: '1' }
    },
    'fade-out': {
      initial: { opacity: '1' },
      animate: { opacity: '0' }
    },
    'slide-up': {
      initial: { opacity: '0', transform: 'translateY(60px)' },
      animate: { opacity: '1', transform: 'translateY(0)' }
    },
    'slide-down': {
      initial: { opacity: '0', transform: 'translateY(-60px)' },
      animate: { opacity: '1', transform: 'translateY(0)' }
    },
    'slide-left': {
      initial: { opacity: '0', transform: 'translateX(60px)' },
      animate: { opacity: '1', transform: 'translateX(0)' }
    },
    'slide-right': {
      initial: { opacity: '0', transform: 'translateX(-60px)' },
      animate: { opacity: '1', transform: 'translateX(0)' }
    },
    'scale-in': {
      initial: { opacity: '0', transform: 'scale(0.8)' },
      animate: { opacity: '1', transform: 'scale(1)' }
    },
    'scale-out': {
      initial: { opacity: '0', transform: 'scale(1.2)' },
      animate: { opacity: '1', transform: 'scale(1)' }
    },
    'rotate-in': {
      initial: { opacity: '0', transform: 'rotate(-10deg) scale(0.9)' },
      animate: { opacity: '1', transform: 'rotate(0deg) scale(1)' }
    },
    'flip-left': {
      initial: { opacity: '0', transform: 'perspective(400px) rotateY(-45deg)' },
      animate: { opacity: '1', transform: 'perspective(400px) rotateY(0deg)' }
    },
    'flip-right': {
      initial: { opacity: '0', transform: 'perspective(400px) rotateY(45deg)' },
      animate: { opacity: '1', transform: 'perspective(400px) rotateY(0deg)' }
    },
    'blur-in': {
      initial: { opacity: '0', filter: 'blur(10px)' },
      animate: { opacity: '1', filter: 'blur(0px)' }
    },
    'zoom-in': {
      initial: { opacity: '0', transform: 'scale(0)' },
      animate: { opacity: '1', transform: 'scale(1)' }
    },
    'zoom-out': {
      initial: { opacity: '0', transform: 'scale(2)' },
      animate: { opacity: '1', transform: 'scale(1)' }
    },
    'bounce-in': {
      initial: { opacity: '0', transform: 'scale(0.3)' },
      animate: { opacity: '1', transform: 'scale(1)' }
    },
    'swing-in': {
      initial: { opacity: '0', transform: 'rotate(-15deg)' },
      animate: { opacity: '1', transform: 'rotate(0deg)' }
    },
    'roll-in': {
      initial: { opacity: '0', transform: 'translateX(-100%) rotate(-120deg)' },
      animate: { opacity: '1', transform: 'translateX(0) rotate(0deg)' }
    }
  };

  function extend(target, source) {
    for (var key in source) {
      if (source.hasOwnProperty(key)) {
        target[key] = source[key];
      }
    }
    return target;
  }

  function debounce(func, wait) {
    var timeout;
    return function() {
      var context = this;
      var args = arguments;
      clearTimeout(timeout);
      timeout = setTimeout(function() {
        func.apply(context, args);
      }, wait);
    };
  }

  function ShopifyScrollFX(options) {
    this.config = extend({}, DEFAULTS);
    this.config = extend(this.config, options || {});
    this.animations = extend({}, ANIMATIONS);
    this.observers = [];
    this.animatedElements = [];
    this.isEnabled = true;
    this.mutationObserver = null;
    
    this.log('Initializing ShopifyScrollFX', this.config);
    
    if (this.shouldDisableAnimations()) {
      this.log('Animations disabled');
      this.isEnabled = false;
      return;
    }
    
    this.init();

  }

  ShopifyScrollFX.prototype.log = function() {
    if (this.config.debug) {
      var args = Array.prototype.slice.call(arguments);
      console.log.apply(console, ['[' + NAMESPACE + ']'].concat(args));
    }
  };

  ShopifyScrollFX.prototype.warn = function() {
    var args = Array.prototype.slice.call(arguments);
    console.warn.apply(console, ['[' + NAMESPACE + ']'].concat(args));
  };

  ShopifyScrollFX.prototype.isMobile = function() {
    return window.innerWidth < this.config.mobileBreakpoint;
  };

  ShopifyScrollFX.prototype.prefersReducedMotion = function() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  ShopifyScrollFX.prototype.shouldDisableAnimations = function() {
    if (this.config.respectReducedMotion && this.prefersReducedMotion()) {
      return true;
    }
    if (this.config.disableOnMobile && this.isMobile()) {
      return true;
    }
    return false;
  };

  ShopifyScrollFX.prototype.init = function() {
    var self = this;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        self.setup();
      });
    } else {
      this.setup();
    }
  };

  ShopifyScrollFX.prototype.setup = function() {
    var self = this;
    this.log('Setting up animations');
    
    this.scanAndInitialize();
    this.observeShopifyChanges();
    
    window.addEventListener('resize', debounce(function() {
      if (self.shouldDisableAnimations() !== !self.isEnabled) {
        self.isEnabled = !self.isEnabled;
        if (self.isEnabled) {
          self.refresh();
        } else {
          self.cleanup();
        }
      }
    }, 150));
    
    this.log('Setup complete');
  };

  ShopifyScrollFX.prototype.scanAndInitialize = function(root) {
    root = root || document;
    var elements = root.querySelectorAll('[' + DATA_ATTR + ']');
    this.log('Found ' + elements.length + ' elements to animate');
    
    for (var i = 0; i < elements.length; i++) {
      if (!elements[i].hasAttribute(INITIALIZED_ATTR)) {
        this.initializeElement(elements[i]);
      }
    }
  };

  ShopifyScrollFX.prototype.parseDataAttributes = function(element) {
    var dataset = element.dataset;
    return {
      animation: dataset.scrollFx || 'fade-in',
      duration: parseInt(dataset.scrollDuration) || this.config.duration,
      delay: parseInt(dataset.scrollDelay) || this.config.delay,
      easing: dataset.scrollEasing || this.config.easing,
      once: dataset.scrollOnce !== 'false',
      threshold: parseFloat(dataset.scrollThreshold) || this.config.threshold,
      stagger: parseInt(dataset.scrollStagger) || this.config.stagger,
      rootMargin: dataset.scrollRootMargin || this.config.rootMargin
    };
  };

  ShopifyScrollFX.prototype.applyStyles = function(element, styles) {
    for (var property in styles) {
      if (styles.hasOwnProperty(property)) {
        element.style[property] = styles[property];
      }
    }
  };

  ShopifyScrollFX.prototype.initializeElement = function(element) {
    var self = this;
    var options = this.parseDataAttributes(element);
    var animation = this.animations[options.animation];
    
    if (!animation) {
      this.warn('Unknown animation type: ' + options.animation);
      return;
    }
    
    element.setAttribute(INITIALIZED_ATTR, 'true');
    
    var initialStyles = extend({}, animation.initial);
    if (this.config.useGPU) {
      initialStyles.willChange = 'transform, opacity';
    }
    this.applyStyles(element, initialStyles);
    
    var finalDelay = options.delay;
    if (options.stagger > 0) {
      var parent = element.parentElement;
      var siblings = parent.querySelectorAll('[' + DATA_ATTR + '="' + options.animation + '"]');
      for (var i = 0; i < siblings.length; i++) {
        if (siblings[i] === element) {
          finalDelay += i * options.stagger;
          break;
        }
      }
    }
    
    var observerOptions = {
      threshold: options.threshold,
      rootMargin: options.rootMargin
    };
    
    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          self.animateElement(entry.target, animation, options, finalDelay);
          
          if (options.once) {
            observer.unobserve(entry.target);
          }
        } else if (!options.once) {
          self.resetElement(entry.target, animation);
        }
      });
    }, observerOptions);
    
    observer.observe(element);
    this.observers.push({ element: element, observer: observer });
  };

  ShopifyScrollFX.prototype.animateElement = function(element, animation, options, delay) {
    var self = this;
    setTimeout(function() {
      var duration = options.duration / 1000;
      element.style.transition = 'all ' + duration + 's ' + options.easing;
      
      self.applyStyles(element, animation.animate);
      self.animatedElements.push(element);
      
      if (self.config.useGPU) {
        setTimeout(function() {
          element.style.willChange = 'auto';
        }, options.duration);
      }
      
      self.log('Animated element:', element, options.animation);
    }, delay);
  };

  ShopifyScrollFX.prototype.resetElement = function(element, animation) {
    element.style.transition = 'none';
    this.applyStyles(element, animation.initial);
    
    var index = this.animatedElements.indexOf(element);
    if (index > -1) {
      this.animatedElements.splice(index, 1);
    }
    
    void element.offsetHeight;
  };

  ShopifyScrollFX.prototype.observeShopifyChanges = function() {
    var self = this;
    
    this.mutationObserver = new MutationObserver(function(mutations) {
      var shouldRescan = false;
      
      mutations.forEach(function(mutation) {
        for (var i = 0; i < mutation.addedNodes.length; i++) {
          var node = mutation.addedNodes[i];
          if (node.nodeType === 1) {
            if (node.hasAttribute && node.hasAttribute(DATA_ATTR)) {
              shouldRescan = true;
            }
            if (node.querySelectorAll) {
              var children = node.querySelectorAll('[' + DATA_ATTR + ']');
              if (children.length > 0) {
                shouldRescan = true;
              }
            }
          }
        }
      });
      
      if (shouldRescan) {
        self.log('DOM changes detected, rescanning...');
        setTimeout(function() {
          self.scanAndInitialize();
        }, 100);
      }
    });
    
    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    document.addEventListener('shopify:section:load', function(e) {
      self.log('Shopify section loaded');
      setTimeout(function() {
        self.scanAndInitialize(e.target);
      }, 100);
    });
    
    document.addEventListener('shopify:section:reorder', function() {
      self.log('Shopify section reordered');
      self.refresh();
    });
  };

  ShopifyScrollFX.prototype.cleanup = function() {
    this.log('Cleaning up all animations');
    
    for (var i = 0; i < this.observers.length; i++) {
      this.observers[i].observer.disconnect();
    }
    
    this.observers = [];
    this.animatedElements = [];
    
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  };

  ShopifyScrollFX.prototype.refresh = function() {
    this.log('Refreshing animations');
    this.cleanup();
    
    var elements = document.querySelectorAll('[' + INITIALIZED_ATTR + ']');
    for (var i = 0; i < elements.length; i++) {
      elements[i].removeAttribute(INITIALIZED_ATTR);
      elements[i].style.transition = '';
      elements[i].style.willChange = '';
    }
    
    this.setup();
  };

  ShopifyScrollFX.prototype.disable = function() {
    this.log('Disabling animations');
    this.isEnabled = false;
    this.cleanup();
    
    var elements = document.querySelectorAll('[' + DATA_ATTR + ']');
    for (var i = 0; i < elements.length; i++) {
      elements[i].style.opacity = '1';
      elements[i].style.transform = 'none';
      elements[i].style.filter = 'none';
    }
  };

  ShopifyScrollFX.prototype.enable = function() {
    this.log('Enabling animations');
    if (!this.shouldDisableAnimations()) {
      this.isEnabled = true;
      this.refresh();
    }
  };

  ShopifyScrollFX.prototype.registerAnimation = function(name, definition) {
    this.log('Registering custom animation: ' + name);
    
    if (!definition.initial || !definition.animate) {
      this.warn('Animation definition must have initial and animate properties');
      return;
    }
    
    this.animations[name] = definition;
  };

  ShopifyScrollFX.prototype.destroy = function() {
    this.log('Destroying ShopifyScrollFX instance');
    this.cleanup();
    
    var elements = document.querySelectorAll('[' + DATA_ATTR + ']');
    for (var i = 0; i < elements.length; i++) {
      elements[i].removeAttribute(INITIALIZED_ATTR);
      elements[i].style.opacity = '';
      elements[i].style.transform = '';
      elements[i].style.transition = '';
      elements[i].style.willChange = '';
      elements[i].style.filter = '';
    }
  };

  window[NAMESPACE] = {
    version: '1.0.0',
    
    init: function(options) {
      if (window[NAMESPACE].instance) {
        window[NAMESPACE].instance.destroy();
      }
      window[NAMESPACE].instance = new ShopifyScrollFX(options);
      return window[NAMESPACE].instance;
    },
    
    refresh: function() {
      if (window[NAMESPACE].instance) {
        window[NAMESPACE].instance.refresh();
      }
    },
    
    disable: function() {
      if (window[NAMESPACE].instance) {
        window[NAMESPACE].instance.disable();
      }
    },
    
    enable: function() {
      if (window[NAMESPACE].instance) {
        window[NAMESPACE].instance.enable();
      }
    },
    
    registerAnimation: function(name, definition) {
      if (window[NAMESPACE].instance) {
        window[NAMESPACE].instance.registerAnimation(name, definition);
      }
    },
    
    destroy: function() {
      if (window[NAMESPACE].instance) {
        window[NAMESPACE].instance.destroy();
        window[NAMESPACE].instance = null;
      }
    }
  };

  if (document.querySelector('[data-scroll-fx-auto]')) {
    window[NAMESPACE].init();
  }

})(window, document);